"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Loader2, ArrowLeft, TrendingUp, AlertTriangle, Gavel, FileText, XCircle, CheckCircle, Scale, Building2, Users } from "lucide-react";
import { Badge } from "../ui/badge";
import { getCompanyName } from "../../lib/textron-data-processor";
import { formatCurrency } from "../../lib/textron-data-processor";

interface DetailedConcludingReviewDashboardProps {
  setActiveSection?: (section: string) => void;
}

interface MaterialityTableData {
  entity: {
    planningMateriality: number;
    revisedMateriality: number | null;
    performanceMateriality: number;
    ctt: number;
  };
  abcotds: Array<{
    name: string;
    planningMateriality: number;
    revisedMateriality: string;
    performanceMateriality: number;
    ctt: string;
  }>;
  components: Array<{
    name: string;
    planningMateriality: number;
    revisedMateriality: string;
    performanceMateriality: number;
    ctt: number;
  }>;
}

interface SignificantRisk {
  rommId: string;
  workspace: string;
  description: string;
  controlIds: string[];
  procedureIds: string[];
}

interface LitigationRow {
  subjectMatter: string;
  forum: string;
  amountInvolved: string;
  description: string;
  managementResponse: string;
  responseOfLegalCounsel: string;
}

interface REMB3Row {
  col_0: string; // Name of estimate
  col_1: string; // ABCOTD
  col_2: string; // Indicative of possible management bias?
}

interface SubsequentEventRow {
  descriptionOfEvent: string;
  requiresAdjustment: string;
  requiresDisclosure: string;
  basisOfAssessment: string;
}

interface CLRNonComplianceRow {
  nature_of_non_compliance: string;
  our_understanding_of_non_compliance: string;
  evaluation_of_possible_effect_on_financial_statements: string;
  evaluation_in_relation_to_other_aspects_of_audit: string;
}

interface WorkspaceSummary {
  workspace: string;
  risks: number;
  controls: number;
  procedures: number;
  deficiencies: number;
  misstatements: number;
}

interface Deficiency {
  id: string;
  deficiencyType: string;
  description: string;
}

interface Misstatement {
  id: string;
  type: string;
  workspace: string;
  amount: number;
}

interface ExternalConfirmationsSummary {
  domainTesting: {
    total: number;
    active: number;
    notFound: number;
    generalDomain: number;
    others: number;
  };
  confirmations: {
    total: number;
    received: number;
    notReceived: number;
    withAlternateTesting: number;
    uniqueWorkspaces: number;
    uniqueSampleSets: number;
  };
  workspaceBreakdown: Array<{
    workspace: string;
    total: number;
    received: number;
    notReceived: number;
  }>;
}

export function DetailedConcludingReviewDashboard({ setActiveSection }: DetailedConcludingReviewDashboardProps = {}) {
  const [companyName, setCompanyName] = useState<string>("");
  const [loading, setLoading] = useState(true);

  // Section 1: Materiality
  const [materialityTableData, setMaterialityTableData] = useState<MaterialityTableData>({
    entity: {
      planningMateriality: 0,
      revisedMateriality: null,
      performanceMateriality: 0,
      ctt: 0,
    },
    abcotds: [],
    components: [],
  });
  const [materialityLoading, setMaterialityLoading] = useState(true);

  // Section 2: Significant Risks
  const [significantRisks, setSignificantRisks] = useState<SignificantRisk[]>([]);
  const [risksLoading, setRisksLoading] = useState(true);

  // Section 3: Review of Estimates for Management Bias - REMB-3 table
  const [remb3Table, setRemb3Table] = useState<REMB3Row[]>([]);
  const [remb3Loading, setRemb3Loading] = useState(true);

  // Section 4: Litigations & Claims
  const [litigationsTable, setLitigationsTable] = useState<LitigationRow[]>([]);
  const [litigationsLoading, setLitigationsLoading] = useState(true);

  // Section 5: Execution Workspace Summary
  const [workspaceSummaries, setWorkspaceSummaries] = useState<WorkspaceSummary[]>([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(true);

  // Section 6: Deficiency Summary
  const [deficiencies, setDeficiencies] = useState<Deficiency[]>([]);
  const [deficiencyLoading, setDeficiencyLoading] = useState(true);

  // Section 7: Misstatement Summary
  const [misstatements, setMisstatements] = useState<Misstatement[]>([]);
  const [misstatementLoading, setMisstatementLoading] = useState(true);
  const [materialityData, setMaterialityData] = useState({
    planningMateriality: 0,
    performanceMateriality: 0,
    clearlyTrivialThreshold: 0,
  });

  // Section 8: External Confirmations Summary
  const [externalConfirmationsLoading, setExternalConfirmationsLoading] = useState(true);
  const [externalConfirmationsSummary, setExternalConfirmationsSummary] = useState<ExternalConfirmationsSummary>({
    domainTesting: { total: 0, active: 0, notFound: 0, generalDomain: 0, others: 0 },
    confirmations: { total: 0, received: 0, notReceived: 0, withAlternateTesting: 0, uniqueWorkspaces: 0, uniqueSampleSets: 0 },
    workspaceBreakdown: [],
  });

  // Section 9: Summary of Subsequent Events
  const [subsequentEventsTable, setSubsequentEventsTable] = useState<SubsequentEventRow[]>([]);
  const [subsequentEventsLoading, setSubsequentEventsLoading] = useState(true);

  // Section 10: Non-Compliance with Laws & Regulations
  const [clrNonComplianceTable, setClrNonComplianceTable] = useState<CLRNonComplianceRow[]>([]);
  const [clrLoading, setClrLoading] = useState(true);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.error("Failed to load company name:", error);
      } finally {
        setLoading(false);
      }
    };
    loadCompanyName();
  }, []);

  // Load Materiality (same as planning review)
  useEffect(() => {
    const loadMateriality = async () => {
      if (!companyName) return;
      setMaterialityLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("Planning_UserResponses.json");
          if (result?.success && result?.data) {
            const data = result.data;
            const dataArray = Array.isArray(data) ? data : [data];
            let materialityResponses: any = null;
            
            for (const sectionData of dataArray) {
              if (sectionData.sectionName === "Materiality" || 
                  sectionData.sectionKey === "materiality" ||
                  (sectionData.responses && (sectionData.responses["M-3"] || sectionData.responses["revisedMateriality"]))) {
                if (!sectionData.companyName || sectionData.companyName === companyName) {
                  materialityResponses = sectionData.responses || {};
                  break;
                }
              }
            }
            
            if (!materialityResponses) {
              const planningAPI = (window as any).planningAPI;
              if (planningAPI?.loadResponses) {
                const sharePointResponses = await planningAPI.loadResponses({
                  companyName,
                  sectionKey: "materiality"
                });
                if (sharePointResponses?.success && sharePointResponses?.data) {
                  materialityResponses = sharePointResponses.data.responses || sharePointResponses.data;
                }
              }
            }
            
            if (materialityResponses) {
              const m3Response = materialityResponses["M-3"];
              let planningMateriality = 0;
              if (m3Response?.textResponse) {
                const textValue = typeof m3Response.textResponse === "string"
                  ? m3Response.textResponse
                  : m3Response.textResponse.plainText || m3Response.textResponse.html || "0";
                planningMateriality = parseFloat(textValue) || 0;
              }

              const revisedMaterialityResponse = materialityResponses["revisedMateriality"];
              const revisedMateriality = revisedMaterialityResponse?.determinedMateriality > 0
                ? revisedMaterialityResponse.determinedMateriality
                : null;

              let performanceMateriality = 0;
              if (revisedMaterialityResponse?.performanceMateriality > 0) {
                performanceMateriality = revisedMaterialityResponse.performanceMateriality;
              } else {
                const m5Response = materialityResponses["M-5"];
                if (m5Response?.overridePerformanceMateriality) {
                  performanceMateriality = parseFloat(m5Response.overridePerformanceMateriality) || 0;
                }
              }

              let ctt = 0;
              if (revisedMaterialityResponse?.trivialThreshold > 0) {
                ctt = revisedMaterialityResponse.trivialThreshold;
              } else {
                const m8Response = materialityResponses["M-8"];
                if (m8Response?.textResponse) {
                  const textValue = typeof m8Response.textResponse === "string"
                    ? m8Response.textResponse
                    : m8Response.textResponse.plainText || m8Response.textResponse.html || "0";
                  ctt = parseFloat(textValue) || 0;
                }
              }

              const abcotds: Array<{
                name: string;
                planningMateriality: number;
                revisedMateriality: string;
                performanceMateriality: number;
                ctt: string;
              }> = [];
              
              const abcotdTable = materialityResponses["abcotdTable"];
              if (abcotdTable?.columns && Array.isArray(abcotdTable.columns)) {
                abcotdTable.columns.forEach((col: any) => {
                  if (col.values) {
                    abcotds.push({
                      name: col.values.abcotd || "",
                      planningMateriality: col.values.determinedMateriality || 0,
                      revisedMateriality: "NA",
                      performanceMateriality: col.values.determinedPM || 0,
                      ctt: "NA",
                    });
                  }
                });
              }

              const components: Array<{
                name: string;
                planningMateriality: number;
                revisedMateriality: string;
                performanceMateriality: number;
                ctt: number;
              }> = [];
              
              const componentsData = materialityResponses["components"];
              if (componentsData && Array.isArray(componentsData)) {
                componentsData.forEach((comp: any) => {
                  if (comp.componentName && comp.table?.rows) {
                    const materialityRow = comp.table.rows.find((r: any) => r.particular === "Materiality");
                    const performanceRow = comp.table.rows.find((r: any) => r.particular === "Performance Materiality");
                    const cttRow = comp.table.rows.find((r: any) => r.particular === "Clearly Trivial Threshold");
                    
                    components.push({
                      name: comp.componentName || "",
                      planningMateriality: materialityRow?.determinedAmount || 0,
                      revisedMateriality: "NA",
                      performanceMateriality: performanceRow?.determinedAmount || 0,
                      ctt: cttRow?.determinedAmount || 0,
                    });
                  }
                });
              }

              setMaterialityTableData({
                entity: {
                  planningMateriality,
                  revisedMateriality,
                  performanceMateriality,
                  ctt,
                },
                abcotds,
                components,
              });

              setMaterialityData({
                planningMateriality,
                performanceMateriality,
                clearlyTrivialThreshold: ctt,
              });
            }
          }
        }
      } catch (error) {
        console.error("Failed to load materiality:", error);
      } finally {
        setMaterialityLoading(false);
      }
    };
    loadMateriality();
  }, [companyName]);

  // Load Significant Risks (same as partner review dashboard)
  useEffect(() => {
    const loadSignificantRisks = async () => {
      if (!companyName) return;
      setRisksLoading(true);
      try {
        // Load ROMMs from Libraries_Romm.json
        let romms: any[] = [];
        if ((window as any).sharePointAPI?.readRommLibrary) {
          const rommRes = await (window as any).sharePointAPI.readRommLibrary();
          if (rommRes.success && rommRes.data?.romm_library) {
            romms = Array.isArray(rommRes.data.romm_library) ? rommRes.data.romm_library : [];
          }
        }

        // Filter for significant risks
        const significant = romms.filter((romm) => {
          const assessment = (romm.assesment || romm.assessment || "").toLowerCase();
          return assessment.includes("significant");
        });

        // Transform to SignificantRisk format
        const risks: SignificantRisk[] = significant.map((romm) => {
          // Get romm_id
          const rommId = romm["romm-id"] || romm.romm_id || "";

          // Get workspace (can be array or string)
          const workspace = Array.isArray(romm.workspace)
            ? romm.workspace[0] || ""
            : romm.workspace || "";

          // Get description
          const description = romm.description || "";

          // Get control_id (can be array or string)
          const controlIds = Array.isArray(romm.control_id)
            ? romm.control_id.map(String)
            : romm.control_id
            ? [String(romm.control_id)]
            : [];

          // Get procedure_id (can be array or string)
          const procedureIds = Array.isArray(romm.procedure_id)
            ? romm.procedure_id.map(String)
            : romm.procedure_id
            ? [String(romm.procedure_id)]
            : [];

          return {
            rommId,
            workspace,
            description,
            controlIds,
            procedureIds,
          };
        });

        setSignificantRisks(risks);
      } catch (error) {
        console.error("Failed to load significant risks:", error);
      } finally {
        setRisksLoading(false);
      }
    };
    loadSignificantRisks();
  }, [companyName]);

  // Load REMB-3 table (Assessment of Management Bias) from ConcludingProcedures_TailoringQuestions_Responses.json
  useEffect(() => {
    const loadREMB3 = async () => {
      if (!companyName) return;
      setRemb3Loading(true);
      try {
        // Load from ConcludingProcedures_TailoringQuestions_Responses.json
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_TailoringQuestions_Responses.json");
          if (result?.success && result.data) {
            // Navigate to "Review of estimates for management bias" workspace -> "REMB-3" question
            const workspaceName = "Review of estimates for management bias";
            const remb3Data = result.data[workspaceName]?.["REMB-3"];
            if (remb3Data?.table && Array.isArray(remb3Data.table)) {
              setRemb3Table(remb3Data.table);
            } else {
              setRemb3Table([]);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load REMB-3 table:", error);
      } finally {
        setRemb3Loading(false);
      }
    };
    loadREMB3();
  }, [companyName]);

  // Load Litigations & Claims (same as partner review dashboard)
  useEffect(() => {
    const loadLitigations = async () => {
      if (!companyName) return;
      setLitigationsLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_LIT_Substantive.json");
          if (result?.success && result.data) {
            const data = result.data;
            // The litigations table is stored in substantiveProcedures.litigations
            const substantiveProcedures = data.substantiveProcedures || {};
            const litigations = substantiveProcedures.litigations;
            if (Array.isArray(litigations)) {
              setLitigationsTable(litigations);
            } else {
              setLitigationsTable([]);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load litigations:", error);
      } finally {
        setLitigationsLoading(false);
      }
    };
    loadLitigations();
  }, [companyName]);

  // Load Execution Workspace Summary (same as partner review dashboard)
  useEffect(() => {
    const loadWorkspaceSummaries = async () => {
      if (!companyName) return;
      setWorkspaceLoading(true);
      try {
        let executionWorkspaces: string[] = [];
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const sectionsRes = await (window as any).sharePointAPI.downloadSectionsJson();
          if (sectionsRes.success && sectionsRes.data?.Execution) {
            executionWorkspaces = Object.keys(sectionsRes.data.Execution);
          }
        }

        let romms: any[] = [];
        if ((window as any).sharePointAPI?.readRommLibrary) {
          const rommRes = await (window as any).sharePointAPI.readRommLibrary();
          if (rommRes.success && rommRes.data?.romm_library) {
            romms = Array.isArray(rommRes.data.romm_library) ? rommRes.data.romm_library : [];
          }
        }

        let controls: any[] = [];
        if ((window as any).sharePointAPI?.downloadRcmJson) {
          const rcmRes = await (window as any).sharePointAPI.downloadRcmJson();
          if (rcmRes.success && rcmRes.data) {
            const librariesRcm = rcmRes.data.libraries_RCM || rcmRes.data.libraries_rcm || {};
            controls = Object.entries(librariesRcm).map(([controlId, entry]: [string, any]) => ({
              controlId,
              workspace: entry.workspace || [],
            }));
          }
        }

        let substantiveProcedures: Record<string, Record<string, any>> = {};
        try {
          const { loadSubstantiveProcedures } = await import("../../utils/substantive-procedures-utils");
          substantiveProcedures = await loadSubstantiveProcedures();
        } catch (error) {
          console.warn("Failed to load SubstantiveProcedures.json:", error);
        }

        let deficienciesByControlId: Array<{ control_id: string[] }> = [];
        if ((window as any).sharePointAPI?.downloadDeficiencyJson) {
          const defRes = await (window as any).sharePointAPI.downloadDeficiencyJson();
          if (defRes?.success && defRes.data?.libraries_deficiency) {
            const raw = defRes.data.libraries_deficiency as Record<string, any>;
            deficienciesByControlId = Object.values(raw).map((v: any) => ({
              control_id: Array.isArray(v.control_id) ? v.control_id : (v.control_id != null ? [String(v.control_id)] : []),
            }));
          }
        }

        let misstatementsByWorkspace: Array<{ workspace: string }> = [];
        if ((window as any).sharePointAPI?.downloadMisstatementJson) {
          const misRes = await (window as any).sharePointAPI.downloadMisstatementJson();
          if (misRes?.success && misRes.data) {
            const lib = misRes.data.Libraries_Misstatement || misRes.data.libraries_Misstatement || {};
            const misObj = lib.misstatements || {};
            misstatementsByWorkspace = Object.values(misObj).map((v: any) => ({
              workspace: v.workspace != null ? String(v.workspace) : "",
            }));
          }
        }

        const summaries: WorkspaceSummary[] = executionWorkspaces.map((workspace) => {
          // Count non-NRPMM risks
          const workspaceRomms = romms.filter((romm) => {
            const workspaceArray = Array.isArray(romm.workspace) ? romm.workspace : romm.workspace ? [romm.workspace] : [];
            return workspaceArray.includes(workspace);
          });

          const nonNrpmmRisks = workspaceRomms.filter((romm) => {
            const assessment = (romm.assesment || romm.assessment || "").toLowerCase();
            return !assessment.includes("nrpmm");
          }).length;

          // Count all controls that have this workspace in their workspace field
          const workspaceControls = controls.filter((c) => {
            const wsArray = Array.isArray(c.workspace) ? c.workspace : c.workspace ? [c.workspace] : [];
            return wsArray.includes(workspace);
          });

          // Count substantive procedures for this workspace from SubstantiveProcedures.json
          const workspaceProcedures = substantiveProcedures[workspace] || {};
          const proceduresPerformed = Object.keys(workspaceProcedures).length;

          // Deficiency count: deficiencies whose control_id matches any control in this workspace
          const workspaceControlIds = workspaceControls.map((c: { controlId: string }) => c.controlId);
          const deficienciesCount = deficienciesByControlId.filter((d) =>
            d.control_id.some((cid) => workspaceControlIds.includes(cid))
          ).length;

          // Misstatement count: misstatements whose workspace field equals this workspace name
          const misstatementsCount = misstatementsByWorkspace.filter((m) => m.workspace === workspace).length;

          return {
            workspace,
            risks: nonNrpmmRisks,
            controls: workspaceControls.length,
            procedures: proceduresPerformed,
            deficiencies: deficienciesCount,
            misstatements: misstatementsCount,
          };
        });

        setWorkspaceSummaries(summaries);
      } catch (error) {
        console.error("Failed to load workspace summaries:", error);
      } finally {
        setWorkspaceLoading(false);
      }
    };
    loadWorkspaceSummaries();
  }, [companyName]);

  // Load Deficiency Summary (from partner review dashboard)
  useEffect(() => {
    const loadDeficiencies = async () => {
      if (!companyName) return;
      setDeficiencyLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadDeficiencyJson) {
          const result = await (window as any).sharePointAPI.downloadDeficiencyJson();
          if (result?.success && result.data?.libraries_deficiency) {
            const raw = result.data.libraries_deficiency as Record<string, any>;
            const deficienciesData: Deficiency[] = Object.entries(raw).map(([id, entry]: [string, any]) => ({
              id,
              deficiencyType: entry.deficiency_type || "N/A",
              description: entry.description || "N/A",
            }));
            setDeficiencies(deficienciesData);
          }
        }
      } catch (error) {
        console.error("Failed to load deficiencies:", error);
      } finally {
        setDeficiencyLoading(false);
      }
    };
    loadDeficiencies();
  }, [companyName]);

  // Load Misstatement Summary (same as partner review dashboard)
  useEffect(() => {
    const loadMisstatements = async () => {
      if (!companyName) return;
      setMisstatementLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadMisstatementJson) {
          const res = await (window as any).sharePointAPI.downloadMisstatementJson();
          if (res.success && res.data) {
            const librariesMisstatement = res.data.Libraries_Misstatement || res.data.libraries_Misstatement || {};
            const misstatementsObj = librariesMisstatement.misstatements || {};
            const items: Misstatement[] = Object.entries(misstatementsObj).map(([id, value]: [string, any]) => {
              // Get the misstatement amount from the type-specific misstatement field
              // Structure: value.factual.misstatement, value.judgmental.misstatement, or value.projected.misstatement
              const type = value.type || "Factual";
              const typeKey = type.toLowerCase(); // "factual", "judgmental", or "projected"
              const typeData = value[typeKey] || {};
              const misstatementAmount = typeData.misstatement || "0";
              
              return {
                id,
                type,
                workspace: value.workspace || "",
                amount: parseFloat(misstatementAmount) || 0,
              };
            });
            setMisstatements(items);
          }
        }
      } catch (error) {
        console.error("Failed to load misstatements:", error);
      } finally {
        setMisstatementLoading(false);
      }
    };
    loadMisstatements();
  }, [companyName]);

  // Load Subsequent Events table (SE-4) from ConcludingProcedures_TailoringQuestions_Responses.json
  useEffect(() => {
    const loadSubsequentEvents = async () => {
      if (!companyName) return;
      setSubsequentEventsLoading(true);
      try {
        // Load from ConcludingProcedures_TailoringQuestions_Responses.json
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_TailoringQuestions_Responses.json");
          if (result?.success && result.data) {
            // Navigate to "Subsequent Events" workspace -> "SE-4" question
            const workspaceName = "Subsequent Events";
            const se4Data = result.data[workspaceName]?.["SE-4"];
            if (se4Data?.table && Array.isArray(se4Data.table)) {
              setSubsequentEventsTable(se4Data.table);
            } else {
              setSubsequentEventsTable([]);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load subsequent events:", error);
      } finally {
        setSubsequentEventsLoading(false);
      }
    };
    loadSubsequentEvents();
  }, [companyName]);

  // Load CLR Non-Compliance table (CLR-4-1-table) from ConcludingProcedures_TailoringQuestions_Responses.json
  useEffect(() => {
    const loadCLRNonCompliance = async () => {
      if (!companyName) return;
      setClrLoading(true);
      try {
        // Load from ConcludingProcedures_TailoringQuestions_Responses.json
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_TailoringQuestions_Responses.json");
          if (result?.success && result.data) {
            // Navigate to "Compliance with laws and regulations" workspace
            const workspaceName = "Compliance with laws and regulations";
            const clr41Response = result.data[workspaceName]?.["CLR-4-1"];
            if (clr41Response?.selectedOption === "Yes") {
              const tableData = result.data[workspaceName]?.["CLR-4-1-table"];
              if (tableData?.table && Array.isArray(tableData.table)) {
                setClrNonComplianceTable(tableData.table);
              } else {
                setClrNonComplianceTable([]);
              }
            } else {
              setClrNonComplianceTable([]);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load CLR non-compliance table:", error);
      } finally {
        setClrLoading(false);
      }
    };
    loadCLRNonCompliance();
  }, [companyName]);

  // Load External Confirmations Summary from confirmation library sources
  useEffect(() => {
    const loadExternalConfirmationsSummary = async () => {
      if (!companyName) return;
      setExternalConfirmationsLoading(true);
      try {
        if (!(window as any).sharePointAPI?.downloadClientJson) {
          return;
        }

        const [domainRes, confirmationRes, activityRes] = await Promise.all([
          (window as any).sharePointAPI.downloadClientJson("Confirmation_DomainTesting.json"),
          (window as any).sharePointAPI.downloadClientJson("Confirmation_Response.json"),
          (window as any).sharePointAPI.downloadClientJson("Confirmation_ActivityLog.json"),
        ]);

        const domainData = domainRes?.success ? domainRes?.data : null;
        const domainRecords = Array.isArray(domainData?.domain_records)
          ? domainData.domain_records
          : Array.isArray(domainData)
            ? domainData
            : (domainData?.domain_info && domainData?.confirming_party_info)
              ? [domainData]
              : [];

        let domainActive = 0;
        let domainNotFound = 0;
        let domainGeneral = 0;
        let domainOthers = 0;

        domainRecords.forEach((record: any) => {
          const status = String(record?.domain_info?.status || "").trim().toLowerCase();
          if (status === "active") {
            domainActive += 1;
          } else if (status === "not found" || status === "notfound") {
            domainNotFound += 1;
          } else if (status === "general domain" || status === "generaldomain") {
            domainGeneral += 1;
          } else if (status) {
            domainOthers += 1;
          }
        });

        const confirmationsData = confirmationRes?.success && confirmationRes?.data && typeof confirmationRes.data === "object"
          ? confirmationRes.data
          : {};
        const timeline = Array.isArray(activityRes?.data?.timeline) ? activityRes.data.timeline : [];

        const receivedConfirmationIds = new Set<string>();
        timeline.forEach((entry: any) => {
          const letterId = String(entry?.letter_id ?? "").trim();
          const action = String(entry?.action ?? "").trim().toLowerCase();
          if (letterId && action === "confirmation received") {
            receivedConfirmationIds.add(letterId);
          }
        });

        const workspaceMap = new Map<string, { total: number; received: number; notReceived: number }>();
        const workspaceSet = new Set<string>();
        const sampleSetSet = new Set<string>();
        let totalConfirmations = 0;
        let receivedCount = 0;
        let alternateTestingCount = 0;

        Object.entries(confirmationsData as Record<string, any>).forEach(([confirmationId, confirmationValue]) => {
          totalConfirmations += 1;
          const partydetails = confirmationValue?.partydetails || {};
          const workspace = String(partydetails?.area || "").trim();
          const sampleSet = String(partydetails?.sample_set || "").trim();
          const isReceived = receivedConfirmationIds.has(String(confirmationId).trim());

          if (isReceived) receivedCount += 1;
          if (confirmationValue?.alternateTesting?.fileName) alternateTestingCount += 1;
          if (workspace) workspaceSet.add(workspace);
          if (sampleSet) sampleSetSet.add(sampleSet);

          if (workspace) {
            const existing = workspaceMap.get(workspace) || { total: 0, received: 0, notReceived: 0 };
            existing.total += 1;
            if (isReceived) {
              existing.received += 1;
            } else {
              existing.notReceived += 1;
            }
            workspaceMap.set(workspace, existing);
          }
        });

        const workspaceBreakdown = Array.from(workspaceMap.entries())
          .map(([workspace, values]) => ({
            workspace,
            total: values.total,
            received: values.received,
            notReceived: values.notReceived,
          }))
          .sort((a, b) => b.total - a.total || a.workspace.localeCompare(b.workspace));

        setExternalConfirmationsSummary({
          domainTesting: {
            total: domainRecords.length,
            active: domainActive,
            notFound: domainNotFound,
            generalDomain: domainGeneral,
            others: domainOthers,
          },
          confirmations: {
            total: totalConfirmations,
            received: receivedCount,
            notReceived: Math.max(totalConfirmations - receivedCount, 0),
            withAlternateTesting: alternateTestingCount,
            uniqueWorkspaces: workspaceSet.size,
            uniqueSampleSets: sampleSetSet.size,
          },
          workspaceBreakdown,
        });
      } catch (error) {
        console.error("Failed to load external confirmations summary:", error);
      } finally {
        setExternalConfirmationsLoading(false);
      }
    };
    loadExternalConfirmationsSummary();
  }, [companyName]);

  if (loading && !companyName) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          <span className="text-white">Loading detailed concluding review dashboard...</span>
        </div>
      </div>
    );
  }

  const totalMisstatementAmount = misstatements.reduce((sum, mis) => sum + Math.abs(mis.amount), 0);

  const handleNavigateToWorkspace = (workspaceName: string) => {
    if (setActiveSection) {
      // Mapping of workspace names to execution section IDs
      const workspaceMap: Record<string, string> = {
        "Employee Benefits Expense": "execution-payroll",
        "Property, Plant & Equipment": "execution-ppe",
        "Intangible Assets": "execution-ia",
        "Depreciation": "execution-depreciation",
        "Other Assets": "execution-other-assets",
        "Other Financial Assets": "execution-other-financial-assets",
        "Inventories": "execution-inv",
        "Cash and Cash Equivalents": "execution-cce",
        "Financial Costs": "execution-fincst",
        "Income Tax": "execution-inctx",
        "Investments": "execution-invsmt",
        "Provisions, Contingent Liabilities and Contingent Assets": "execution-pcl",
        "Lease Liability": "execution-lease-liability",
        "Lease Liability - Ind AS": "execution-lease-liability-ind-as",
        "Right-of-Use Assets": "execution-right-of-use-assets",
        "Impairment": "execution-impairment",
        "Trade Receivables": "execution-trade-receivables",
        "Borrowings": "execution-borrowings",
        "Other Expenses": "execution-other-expenses",
        "Other Liabilities": "execution-other-liabilities",
        "Other Financial Liabilities": "execution-other-financial-liabilities",
        "Other Receivables": "execution-other-receivables",
        "Going Concern": "execution-going-concern",
        "Litigations & Claims": "execution-litigations-claims",
        "Opening Balance Testing": "execution-opening-balance-testing",
        "Related Party Disclosure": "execution-related-party-disclosure",
        "Equity": "execution-equity",
        "Management Use of Expert": "execution-management-use-of-expert",
        "Use of Auditors Specialist": "execution-use-of-auditors-specialist",
        "Compliance with Laws and Regulations": "execution-compliance-with-laws-and-regulations",
        "Review of Estimates for Management Bias": "execution-review-of-estimates-for-management-bias",
        "Internal Audit": "execution-internal-audit",
        "Capital or Other Maintenance": "execution-comc",
        "Financial Closing & Reporting Process": "execution-fcrp",
        "Management Override of Controls": "execution-moc",
      };
      
      const section = workspaceMap[workspaceName];
      if (section) {
        setActiveSection(section);
      } else {
        // For dynamic workspaces, encode the workspace name
        // Format: dynamic-workspace:Execution:prefix:encodedName
        const encodedName = encodeURIComponent(workspaceName);
        setActiveSection(`dynamic-workspace:Execution::${encodedName}`);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Detailed Concluding Procedure Review Dashboard</h1>
            <p className="text-white/60 mt-1">Comprehensive concluding procedures review</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setActiveSection?.("partner-review-concluding")}
            className="border-white/20 bg-white/5 text-white hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Concluding Review
          </Button>
        </div>

        {/* 1. Materiality Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500/30 to-purple-600/20 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Materiality Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Planning and performance materiality summary</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {materialityLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/80 font-medium">Entity/ABCOTD/Component</TableHead>
                      <TableHead className="text-white/80 font-medium text-right">Planning Materiality</TableHead>
                      <TableHead className="text-white/80 font-medium text-right">Revised Materiality</TableHead>
                      <TableHead className="text-white/80 font-medium text-right">Performance Materiality</TableHead>
                      <TableHead className="text-white/80 font-medium text-right">CTT</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="border-white/10">
                      <TableCell className="text-white font-medium">Entity</TableCell>
                      <TableCell className="text-white text-right">{formatCurrency(materialityTableData.entity.planningMateriality)}</TableCell>
                      <TableCell className="text-white text-right">
                        {materialityTableData.entity.revisedMateriality ? formatCurrency(materialityTableData.entity.revisedMateriality) : "N/A"}
                      </TableCell>
                      <TableCell className="text-white text-right">{formatCurrency(materialityTableData.entity.performanceMateriality)}</TableCell>
                      <TableCell className="text-white text-right">{formatCurrency(materialityTableData.entity.ctt)}</TableCell>
                    </TableRow>
                    {materialityTableData.abcotds.map((abcotd, index) => (
                      <TableRow key={`abcotd-${index}`} className="border-white/10">
                        <TableCell className="text-white/90">{abcotd.name}</TableCell>
                        <TableCell className="text-white/90 text-right">{formatCurrency(abcotd.planningMateriality)}</TableCell>
                        <TableCell className="text-white/90 text-right">{abcotd.revisedMateriality}</TableCell>
                        <TableCell className="text-white/90 text-right">{formatCurrency(abcotd.performanceMateriality)}</TableCell>
                        <TableCell className="text-white/90 text-right">{abcotd.ctt}</TableCell>
                      </TableRow>
                    ))}
                    {materialityTableData.components.map((component, index) => (
                      <TableRow key={`component-${index}`} className="border-white/10">
                        <TableCell className="text-white/90">{component.name}</TableCell>
                        <TableCell className="text-white/90 text-right">{formatCurrency(component.planningMateriality)}</TableCell>
                        <TableCell className="text-white/90 text-right">{component.revisedMateriality}</TableCell>
                        <TableCell className="text-white/90 text-right">{formatCurrency(component.performanceMateriality)}</TableCell>
                        <TableCell className="text-white/90 text-right">{formatCurrency(component.ctt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 2. Significant Risks Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500/30 to-red-600/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Significant Risks Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Risks assessed as significant</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {risksLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : significantRisks.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Risk ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Workspace</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Description</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Control(s)</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Substantive Procedures</th>
                    </tr>
                  </thead>
                  <tbody>
                    {significantRisks.map((risk) => (
                      <tr
                        key={risk.rommId}
                        className="border-b border-white/5 hover:bg-white/5"
                      >
                        <td className="py-3 px-4 text-white font-medium">{risk.rommId}</td>
                        <td className="py-3 px-4 text-white/80">{risk.workspace || "N/A"}</td>
                        <td className="py-3 px-4 text-white/70">{risk.description || "N/A"}</td>
                        <td className="py-3 px-4 text-white/70">
                          {risk.controlIds.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {risk.controlIds.map((controlId, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs border-white/20 text-white/70">
                                  {controlId}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td className="py-3 px-4 text-white/70">
                          {risk.procedureIds.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {risk.procedureIds.map((procedureId, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs border-white/20 text-white/70">
                                  {procedureId}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No significant risks found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3. Review of Estimates for Management Bias - REMB-3 */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500/30 to-blue-600/20 flex items-center justify-center">
                <Scale className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Review of Estimates for Management Bias</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Assessment of Management Bias</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {remb3Loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : remb3Table.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/80 font-medium">Name of Estimate</TableHead>
                      <TableHead className="text-white/80 font-medium">ABCOTD</TableHead>
                      <TableHead className="text-white/80 font-medium">Indicative of possible management bias?</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {remb3Table.map((row, index) => (
                      <TableRow key={index} className="border-white/10">
                        <TableCell className="text-white/90">{row.col_0 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_1 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_2 || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 4. Litigations & Claims Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500/30 to-orange-600/20 flex items-center justify-center">
                <Gavel className="h-5 w-5 text-orange-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Litigations & Claims Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Litigations from Assessment</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {litigationsLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : litigationsTable.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/80 font-medium">Subject Matter</TableHead>
                      <TableHead className="text-white/80 font-medium">Forum</TableHead>
                      <TableHead className="text-white/80 font-medium">Amount Involved</TableHead>
                      <TableHead className="text-white/80 font-medium">Description</TableHead>
                      <TableHead className="text-white/80 font-medium">Management Response</TableHead>
                      <TableHead className="text-white/80 font-medium">Response of Legal Counsel</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {litigationsTable.map((row, index) => (
                      <TableRow key={index} className="border-white/10">
                        <TableCell className="text-white/90">{row.subjectMatter || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.forum || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.amountInvolved || "-"}</TableCell>
                        <TableCell className="text-white/90 max-w-xs">
                          <div className="line-clamp-2">{row.description || "-"}</div>
                        </TableCell>
                        <TableCell className="text-white/90 max-w-xs">
                          <div className="line-clamp-2">{row.managementResponse || "-"}</div>
                        </TableCell>
                        <TableCell className="text-white/90 max-w-xs">
                          <div className="line-clamp-2">{row.responseOfLegalCounsel || "-"}</div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No litigations found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 5. Execution Workspace Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-green-500/30 to-green-600/20 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Execution Workspace Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Summary by workspace</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {workspaceLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : workspaceSummaries.length === 0 ? (
              <div className="py-8 text-center text-white/50 text-sm">
                No execution workspace data available
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/80 font-semibold">Workspace</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-28">RoMMs</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-28">Controls</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-40 whitespace-normal px-2">Substantive Procedures</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-32">Deficiencies</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-28">Misstatements</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workspaceSummaries.map((summary) => (
                      <TableRow
                        key={summary.workspace}
                        className="border-white/5 hover:bg-white/5"
                      >
                        <TableCell
                          className="text-[#4da3ff] font-medium cursor-pointer hover:underline hover:text-[#4da3ff]/90"
                          onClick={() => handleNavigateToWorkspace(summary.workspace)}
                        >
                          {summary.workspace}
                        </TableCell>
                        <TableCell className="text-white/90 text-center w-28 tabular-nums">{summary.risks}</TableCell>
                        <TableCell className="text-white/90 text-center w-28 tabular-nums">{summary.controls}</TableCell>
                        <TableCell className="text-white/90 text-center w-40 tabular-nums">{summary.procedures}</TableCell>
                        <TableCell className="text-white/90 text-center w-32 tabular-nums">{summary.deficiencies}</TableCell>
                        <TableCell className="text-white/90 text-center w-28 tabular-nums">{summary.misstatements}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 6. Deficiency Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500/30 to-red-600/20 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Deficiency Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Deficiencies Identified</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {deficiencyLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : deficiencies.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/80 font-medium">Deficiency ID</TableHead>
                      <TableHead className="text-white/80 font-medium">Deficiency Type</TableHead>
                      <TableHead className="text-white/80 font-medium">Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deficiencies.map((def) => (
                      <TableRow key={def.id} className="border-white/10">
                        <TableCell className="text-white/90">{def.id}</TableCell>
                        <TableCell className="text-white/90">{def.deficiencyType}</TableCell>
                        <TableCell className="text-white/90">{def.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No deficiencies found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 7. Misstatement Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-yellow-500/30 to-yellow-600/20 flex items-center justify-center">
                <FileText className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Misstatement Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Identified Misstatements</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {misstatementLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : misstatements.length > 0 ? (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-white/80 font-medium">Misstatement ID</TableHead>
                        <TableHead className="text-white/80 font-medium">Type</TableHead>
                        <TableHead className="text-white/80 font-medium">Workspace</TableHead>
                        <TableHead className="text-white/80 font-medium text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {misstatements.map((mis) => (
                        <TableRow key={mis.id} className="border-white/10">
                          <TableCell className="text-white/90">{mis.id}</TableCell>
                          <TableCell className="text-white/90">{mis.type}</TableCell>
                          <TableCell className="text-white/90">{mis.workspace}</TableCell>
                          <TableCell className="text-white/90 text-right">{formatCurrency(mis.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-white/60 mb-1">Total Misstatement Amount</p>
                      <p className="text-lg font-bold text-white">{formatCurrency(totalMisstatementAmount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/60 mb-1">Planning Materiality</p>
                      <p className="text-lg font-bold text-white">{formatCurrency(materialityData.planningMateriality)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/60 mb-1">Performance Materiality</p>
                      <p className="text-lg font-bold text-white">{formatCurrency(materialityData.performanceMateriality)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/60 mb-1">Clearly Trivial Threshold</p>
                      <p className="text-lg font-bold text-white">{formatCurrency(materialityData.clearlyTrivialThreshold)}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No misstatements found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 8. External Confirmations Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500/30 to-indigo-600/20 flex items-center justify-center">
                <Users className="h-5 w-5 text-indigo-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">External Confirmations Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Domain testing and confirmations overview</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {externalConfirmationsLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-xs text-white/60 mb-3 uppercase tracking-wide">Domain Testing</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-white/60">Total records</p>
                        <p className="text-xl font-semibold text-white">{externalConfirmationsSummary.domainTesting.total}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">Active</p>
                        <p className="text-xl font-semibold text-emerald-400">{externalConfirmationsSummary.domainTesting.active}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">Not Found</p>
                        <p className="text-xl font-semibold text-rose-400">{externalConfirmationsSummary.domainTesting.notFound}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">General Domain</p>
                        <p className="text-xl font-semibold text-amber-400">{externalConfirmationsSummary.domainTesting.generalDomain}</p>
                      </div>
                    </div>
                    {externalConfirmationsSummary.domainTesting.others > 0 && (
                      <p className="text-xs text-white/60 mt-3">
                        Other status records: <span className="text-white">{externalConfirmationsSummary.domainTesting.others}</span>
                      </p>
                    )}
                  </div>

                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-xs text-white/60 mb-3 uppercase tracking-wide">Confirmations</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-white/60">Total confirmations</p>
                        <p className="text-xl font-semibold text-white">{externalConfirmationsSummary.confirmations.total}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">Received</p>
                        <p className="text-xl font-semibold text-emerald-400">{externalConfirmationsSummary.confirmations.received}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">Not received</p>
                        <p className="text-xl font-semibold text-rose-400">{externalConfirmationsSummary.confirmations.notReceived}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">Alt testing linked</p>
                        <p className="text-xl font-semibold text-indigo-400">{externalConfirmationsSummary.confirmations.withAlternateTesting}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant="outline" className="border-white/20 text-white/80">
                        Workspaces: {externalConfirmationsSummary.confirmations.uniqueWorkspaces}
                      </Badge>
                      <Badge variant="outline" className="border-white/20 text-white/80">
                        Sample Sets: {externalConfirmationsSummary.confirmations.uniqueSampleSets}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-white/60 mb-3 uppercase tracking-wide">Workspace-wise Confirmation Status</p>
                  {externalConfirmationsSummary.workspaceBreakdown.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/10">
                            <TableHead className="text-white/80 font-medium">Workspace</TableHead>
                            <TableHead className="text-white/80 font-medium text-right">Total</TableHead>
                            <TableHead className="text-white/80 font-medium text-right">Received</TableHead>
                            <TableHead className="text-white/80 font-medium text-right">Not Received</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {externalConfirmationsSummary.workspaceBreakdown.map((row) => (
                            <TableRow key={row.workspace} className="border-white/10">
                              <TableCell className="text-white/90">{row.workspace}</TableCell>
                              <TableCell className="text-white/90 text-right">{row.total}</TableCell>
                              <TableCell className="text-emerald-400 text-right">{row.received}</TableCell>
                              <TableCell className="text-rose-400 text-right">{row.notReceived}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-white/50 text-sm">No confirmation entries found.</div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 9. Summary of Subsequent Events */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-cyan-500/30 to-cyan-600/20 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Summary of Subsequent Events</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">List of events</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {subsequentEventsLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : subsequentEventsTable.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/80 font-medium">Description of Event</TableHead>
                      <TableHead className="text-white/80 font-medium">Requires Adjustment?</TableHead>
                      <TableHead className="text-white/80 font-medium">Requires Disclosure?</TableHead>
                      <TableHead className="text-white/80 font-medium">Basis of Assessment</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subsequentEventsTable.map((row, index) => (
                      <TableRow key={index} className="border-white/10">
                        <TableCell className="text-white/90">{row.descriptionOfEvent || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.requiresAdjustment || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.requiresDisclosure || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.basisOfAssessment || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No subsequent events found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 10. Non-Compliance with Laws & Regulations */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-pink-500/30 to-pink-600/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-pink-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Non-Compliance with Laws & Regulations</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Non-compliance instances</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {clrLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : clrNonComplianceTable.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/80 font-medium">Nature of Non-Compliance</TableHead>
                      <TableHead className="text-white/80 font-medium">Our Understanding</TableHead>
                      <TableHead className="text-white/80 font-medium">Effect on Financial Statements</TableHead>
                      <TableHead className="text-white/80 font-medium">Evaluation in Relation to Other Aspects</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clrNonComplianceTable.map((row, index) => (
                      <TableRow key={index} className="border-white/10">
                        <TableCell className="text-white/90">{row.nature_of_non_compliance || "-"}</TableCell>
                        <TableCell className="text-white/90 max-w-xs">
                          <div className="line-clamp-3">{row.our_understanding_of_non_compliance || "-"}</div>
                        </TableCell>
                        <TableCell className="text-white/90 max-w-xs">
                          <div className="line-clamp-3">{row.evaluation_of_possible_effect_on_financial_statements || "-"}</div>
                        </TableCell>
                        <TableCell className="text-white/90 max-w-xs">
                          <div className="line-clamp-3">{row.evaluation_in_relation_to_other_aspects_of_audit || "-"}</div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No non-compliance instances recorded</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
